



































import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import CarrierProductFormInfo from '@/modules/CarrierProduct/components/forms/CarrierProductFormInfo.vue'
import CarrierProductFormInsurance from '@/modules/CarrierProduct/components/forms/CarrierProductFormInsurance.vue'

import { newCarrierProduct, editCarrierProduct } from '@/modules/CarrierProduct/gql/carrier-product.gql'
import { ICarrierProductInfoInput, ICarrierProductInsuranceInput } from '@/modules/CarrierProduct/interfaces/carrier-product-form'
import { CarrierProduct } from '@/modules/CarrierProduct/interfaces/carrier-product'
import { documentDeliveryKeys, quoteViaKeys, bindIssueViaKeys, lineOfBusinessCodeKeys, yesOrNoKeys } from '@/modules/Carrier/helpers/carrier'

@Component({
  components: {
    CarrierProductFormInfo,
    AppDialog,
    CarrierProductFormInsurance,
    AppSteps
  }
})
export default class CarrierProductForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => CarrierProduct }) readonly carrierProduct!: CarrierProduct

  showDialog = false
  loading = false

  stepIndex = 0
  steps = [
    ['Information', 770, 401],
    ['Product Info', 770, 441]
  ]

  infoInput: ICarrierProductInfoInput = {
    name: '',
    notes: '',
    naicCode: '',
    lineOfBusinessCodeIdx: -1
  }

  productInput: ICarrierProductInsuranceInput = {
    downloadsIdx: -1,
    policyDocumentsDeliveryIdx: -1,
    endorsementDocumentDeliveryIdx: -1,
    quoteViaIdx: -1,
    agencyBillIdx: -1,
    bindIssueViaIdx: -1
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.carrierProduct
        ? 'Save'
        : 'Create'
  }

  get confirmAheadText () {
    return this.carrierProduct ? 'Save' : 'Create'
  }

  get canPost () {
    return this.stepIndex < this.steps.length - 1
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.carrierProduct) {
      const {
        name, notes,
        naicCode, lineOfBusinessCode, downloads, policyDocumentsDelivery, endorsementDocumentDelivery, quoteVia, agencyBill, bindIssueVia
      } = this.carrierProduct

      this.infoInput = {
        name,
        notes,
        naicCode,
        lineOfBusinessCodeIdx: lineOfBusinessCodeKeys.findIndex(key => key === lineOfBusinessCode)
      }
      this.productInput = {
        downloadsIdx: yesOrNoKeys.findIndex(key => key === downloads),
        policyDocumentsDeliveryIdx: documentDeliveryKeys.findIndex(key => key === policyDocumentsDelivery),
        endorsementDocumentDeliveryIdx: documentDeliveryKeys.findIndex(key => key === endorsementDocumentDelivery),
        quoteViaIdx: quoteViaKeys.findIndex(key => key === quoteVia),
        agencyBillIdx: yesOrNoKeys.findIndex(key => key === agencyBill),
        bindIssueViaIdx: bindIssueViaKeys.findIndex(key => key === bindIssueVia)
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.carrierProduct) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.carrierProduct) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    }
  }

  async handleCreate () {
    const {
      infoInput: {
        name, notes, naicCode, lineOfBusinessCodeIdx
      },
      productInput: {
        downloadsIdx, policyDocumentsDeliveryIdx, endorsementDocumentDeliveryIdx, quoteViaIdx, agencyBillIdx, bindIssueViaIdx
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: newCarrierProduct,
        variables: {
          carrierProductInput: {
            name,
            notes,
            naicCode,
            lineOfBusinessCode: lineOfBusinessCodeKeys[lineOfBusinessCodeIdx] || null,
            downloads: yesOrNoKeys[downloadsIdx] || null,
            policyDocumentsDelivery: documentDeliveryKeys[policyDocumentsDeliveryIdx] || null,
            endorsementDocumentDelivery: documentDeliveryKeys[endorsementDocumentDeliveryIdx] || null,
            quoteVia: quoteViaKeys[quoteViaIdx] || null,
            agencyBill: yesOrNoKeys[agencyBillIdx] || null,
            bindIssueVia: bindIssueViaKeys[bindIssueViaIdx] || null
          }
        }
      })
      this.clearForm()
      this.onSuccess('Create the new insurance product successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      infoInput: {
        name, notes, naicCode, lineOfBusinessCodeIdx
      },
      productInput: {
        downloadsIdx, policyDocumentsDeliveryIdx, endorsementDocumentDeliveryIdx, quoteViaIdx, agencyBillIdx, bindIssueViaIdx
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: editCarrierProduct,
        variables: {
          carrierProductId: +this.carrierProduct.id,
          carrierProductInput: {
            name,
            notes,
            naicCode,
            lineOfBusinessCode: lineOfBusinessCodeKeys[lineOfBusinessCodeIdx] || null,
            downloads: yesOrNoKeys[downloadsIdx] || null,
            policyDocumentsDelivery: documentDeliveryKeys[policyDocumentsDeliveryIdx] || null,
            endorsementDocumentDelivery: documentDeliveryKeys[endorsementDocumentDeliveryIdx] || null,
            quoteVia: quoteViaKeys[quoteViaIdx] || null,
            agencyBill: yesOrNoKeys[agencyBillIdx] || null,
            bindIssueVia: bindIssueViaKeys[bindIssueViaIdx] || null
          }
        }
      })
      this.clearForm()
      this.onSuccess('Update insurance product successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      notes: '',
      naicCode: '',
      lineOfBusinessCodeIdx: -1
    }

    this.productInput = {
      downloadsIdx: -1,
      policyDocumentsDeliveryIdx: -1,
      endorsementDocumentDeliveryIdx: -1,
      quoteViaIdx: -1,
      agencyBillIdx: -1,
      bindIssueViaIdx: -1
    }
  }

  @Emit('hide')
  handleHide () {
    this.$emit('input', false)
  }

  @Emit('cancel')
  handleCancel () {
    this.handleHide()
    this.clearForm()
  }

  @Emit('success')
  @Emit('hide')
  onSuccess (msg = '') {
    this.handleHide()
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
